import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Download, CheckCircle, Loader, CloudOff } from 'lucide-react'
import { engineApiClient } from '@/lib/apiClient'
import type { ScorecardCriterion, ScorecardResponses } from '@/lib/contracts'
import { exportSectionToWord } from '@/lib/exportToWord'

// ─── Constants ────────────────────────────────────────────────────────────────

type Recommendation = ScorecardResponses['recommendation']

const RECOMMENDATION_OPTIONS: {
  value: Recommendation
  label: string
  color: string
  bg: string
  border: string
}[] = [
  { value: 'strong-yes', label: 'Strong Yes', color: 'text-green-400',  bg: 'bg-green-500/15',  border: 'border-green-500/40' },
  { value: 'yes',        label: 'Yes',        color: 'text-blue-400',   bg: 'bg-blue-500/15',   border: 'border-blue-500/40' },
  { value: 'maybe',      label: 'Maybe',      color: 'text-yellow-400', bg: 'bg-yellow-500/15', border: 'border-yellow-500/40' },
  { value: 'no',         label: 'No',         color: 'text-red-400',    bg: 'bg-red-500/15',    border: 'border-red-500/40' },
]

const SCORE_LABELS: Record<number, string> = {
  1: 'Poor',
  2: 'Below avg',
  3: 'Average',
  4: 'Good',
  5: 'Excellent',
}

// ─── Criteria builder ─────────────────────────────────────────────────────────
//
// Priority order:
//   1. Must-haves (always first — highest signal)
//   2. Leadership signals (role-specific context)
//   3. Nice-to-haves (if slots remain)
//   4. Smart defaults to fill to 6–7 criteria
//
// Deduplicates, caps at 8 criteria total.

const SMART_DEFAULTS: Omit<ScorecardCriterion, 'score' | 'notes'>[] = [
  {
    id: 'commercial-acumen',
    title: 'Commercial Acumen',
    description: 'Evidence of revenue ownership, commercial thinking, and measurable results',
  },
  {
    id: 'stage-fit',
    title: 'Stage Fit',
    description: 'Experience that translates directly to this company\'s current stage and context',
  },
  {
    id: 'leadership',
    title: 'Leadership & Team Building',
    description: 'Ability to hire, develop, and retain high-performing teams',
  },
  {
    id: 'communication',
    title: 'Communication & Executive Presence',
    description: 'Structured thinking, clear communication, ability to influence at senior level',
  },
  {
    id: 'motivation',
    title: 'Motivation & Cultural Fit',
    description: 'Genuine interest in the role and alignment with the company\'s mission and values',
  },
]

function buildCriteria(
  mustHaves: string[],
  leadershipSignals: string[],
  niceToHaves: string[],
): ScorecardCriterion[] {
  const criteria: ScorecardCriterion[] = []
  const seen = new Set<string>()

  const add = (title: string, description: string, id: string) => {
    const key = title.toLowerCase().slice(0, 30)
    if (!seen.has(key) && criteria.length < 8) {
      seen.add(key)
      criteria.push({ id, title, description, score: null, notes: '' })
    }
  }

  // Must-haves first
  mustHaves.slice(0, 4).forEach((mh, i) =>
    add(mh, 'Must-have from role brief', `mh-${i}`)
  )

  // Leadership signals
  leadershipSignals.slice(0, 2).forEach((ls, i) =>
    add(ls, 'Leadership signal from intake', `ls-${i}`)
  )

  // Nice-to-haves
  niceToHaves.slice(0, 2).forEach((nth, i) =>
    add(nth, 'Nice-to-have from role brief', `nth-${i}`)
  )

  // Fill remaining slots with smart defaults
  for (const def of SMART_DEFAULTS) {
    if (criteria.length >= 6) break
    add(def.title, def.description, def.id)
  }

  return criteria
}

// ─── Export helper ────────────────────────────────────────────────────────────

function buildExportText(state: ScorecardResponses): string {
  const scored = state.criteria.filter((c) => c.score !== null)
  const totalScore = scored.reduce((sum, c) => sum + (c.score ?? 0), 0)
  const maxScore = state.criteria.length * 5
  const pct = maxScore > 0 ? Math.round((totalScore / maxScore) * 100) : 0
  const recLabel =
    RECOMMENDATION_OPTIONS.find((r) => r.value === state.recommendation)?.label ?? '—'

  return [
    '# Interview Scorecard',
    '',
    `Candidate: ${state.candidateName || '—'}`,
    `Current Role: ${state.currentRole || '—'}`,
    `Interview Date: ${state.interviewDate || '—'}`,
    `Interviewer: ${state.interviewer || '—'}`,
    '',
    '---',
    '',
    '## Evaluation Criteria',
    '',
    ...state.criteria.map((c) =>
      [
        `### ${c.title}`,
        c.description,
        `Score: ${c.score !== null ? `${c.score}/5 — ${SCORE_LABELS[c.score]}` : 'Not scored'}`,
        c.notes ? `Notes: ${c.notes}` : '',
        '',
      ]
        .filter(Boolean)
        .join('\n')
    ),
    '---',
    '',
    '## Overall Assessment',
    '',
    `Total Score: ${totalScore} / ${maxScore} (${pct}%)`,
    `Recommendation: ${recLabel}`,
  ].join('\n')
}

// ─── Score button ─────────────────────────────────────────────────────────────

const ScoreButton = ({
  value,
  selected,
  onClick,
}: {
  value: number
  selected: boolean
  onClick: () => void
}) => (
  <button
    type="button"
    onClick={onClick}
    title={SCORE_LABELS[value]}
    className={`
      w-9 h-9 rounded-lg text-sm font-bold transition-all
      ${
        selected
          ? 'bg-[#FD802E] text-[#233D4C] shadow-sm shadow-[#FD802E]/30 scale-105'
          : 'bg-[#02182B] border border-[#2a4a5c] text-gray-400 hover:border-[#FD802E]/50 hover:text-[#FD802E]'
      }
    `}
  >
    {value}
  </button>
)

// ─── Main component ───────────────────────────────────────────────────────────

interface ScorecardUIProps {
  projectId: string
  mustHaves?: string[]
  leadershipSignals?: string[]
  niceToHaves?: string[]
}

export const ScorecardUI: React.FC<ScorecardUIProps> = ({
  projectId,
  mustHaves = [],
  leadershipSignals = [],
  niceToHaves = [],
}) => {
  const freshCriteria = buildCriteria(mustHaves, leadershipSignals, niceToHaves)

  const blankState = (): ScorecardResponses => ({
    candidateName: '',
    currentRole: '',
    interviewDate: new Date().toISOString().split('T')[0],
    interviewer: '',
    criteria: freshCriteria,
    recommendation: null,
    savedAt: '',
  })

  const [state, setState] = useState<ScorecardResponses>(blankState)
  const [isLoading, setIsLoading] = useState(true)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Load from API on mount
  useEffect(() => {
    setIsLoading(true)
    engineApiClient
      .getScorecardResponses(projectId)
      .then((res) => {
        if (res.responses) {
          // Merge saved scores/notes into fresh criteria (handles new criteria from role updates)
          const savedMap = new Map(res.responses.criteria.map((c) => [c.id, c]))
          const merged = freshCriteria.map((c) => {
            const saved = savedMap.get(c.id)
            return saved ? { ...c, score: saved.score, notes: saved.notes } : c
          })
          setState({
            ...res.responses,
            criteria: merged,
          })
        }
      })
      .catch(() => {
        // No saved state — start fresh (silent)
      })
      .finally(() => setIsLoading(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId])

  // Auto-save with debounce whenever state changes
  const autoSave = useCallback(
    (nextState: ScorecardResponses) => {
      if (saveTimer.current) clearTimeout(saveTimer.current)
      setSaveStatus('saving')
      saveTimer.current = setTimeout(() => {
        const payload: ScorecardResponses = {
          ...nextState,
          savedAt: new Date().toISOString(),
        }
        engineApiClient
          .saveScorecardResponses(projectId, payload)
          .then(() => setSaveStatus('saved'))
          .catch(() => setSaveStatus('error'))
      }, 800)
    },
    [projectId]
  )

  const update = (patch: Partial<ScorecardResponses>) => {
    setState((prev) => {
      const next = { ...prev, ...patch }
      autoSave(next)
      return next
    })
  }

  const updateCriterionScore = (id: string, score: number) => {
    setState((prev) => {
      const next = {
        ...prev,
        criteria: prev.criteria.map((c) =>
          c.id === id ? { ...c, score: c.score === score ? null : score } : c
        ),
      }
      autoSave(next)
      return next
    })
  }

  const updateCriterionNotes = (id: string, notes: string) => {
    setState((prev) => {
      const next = {
        ...prev,
        criteria: prev.criteria.map((c) =>
          c.id === id ? { ...c, notes } : c
        ),
      }
      autoSave(next)
      return next
    })
  }

  const handleExport = () => {
    exportSectionToWord('Interview Scorecard', buildExportText(state))
  }

  // Scoring summary
  const scored = state.criteria.filter((c) => c.score !== null)
  const totalScore = scored.reduce((sum, c) => sum + (c.score ?? 0), 0)
  const maxScore = state.criteria.length * 5
  const scorePct = maxScore > 0 ? Math.round((totalScore / maxScore) * 100) : 0
  const scoreColor =
    scorePct >= 70 ? 'text-green-400' : scorePct >= 50 ? 'text-[#FD802E]' : 'text-red-400'
  const barColor =
    scorePct >= 70 ? 'bg-green-500' : scorePct >= 50 ? 'bg-[#FD802E]' : 'bg-red-500'

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
        <Loader size={18} className="animate-spin text-[#FD802E]" />
        <span className="text-sm">Loading scorecard…</span>
      </div>
    )
  }

  return (
    <div className="space-y-5 py-2">

      {/* Save status indicator */}
      <div className="flex items-center justify-end gap-2 h-5">
        {saveStatus === 'saving' && (
          <span className="text-xs text-gray-500 flex items-center gap-1.5">
            <Loader size={11} className="animate-spin" /> Saving…
          </span>
        )}
        {saveStatus === 'saved' && (
          <span className="text-xs text-green-500 flex items-center gap-1.5">
            <CheckCircle size={11} /> Saved to project
          </span>
        )}
        {saveStatus === 'error' && (
          <span className="text-xs text-red-400 flex items-center gap-1.5">
            <CloudOff size={11} /> Save failed
          </span>
        )}
      </div>

      {/* ── Candidate Information ──────────────────────────────── */}
      <div className="bg-[#02182B] border border-[#2a4a5c] rounded-xl p-5">
        <h3 className="text-xs uppercase text-gray-500 font-semibold tracking-wide mb-4">
          Candidate Information
        </h3>
        <div className="grid sm:grid-cols-2 gap-4">
          {(
            [
              { field: 'candidateName' as const, label: 'Candidate Name',  placeholder: 'Full name' },
              { field: 'currentRole'   as const, label: 'Current Role',    placeholder: 'Title, Company' },
              { field: 'interviewDate' as const, label: 'Interview Date',  placeholder: '', type: 'date' },
              { field: 'interviewer'   as const, label: 'Interviewer',     placeholder: 'Your name' },
            ] as const
          ).map(({ field, label, placeholder, type }) => (
            <div key={field}>
              <label className="block text-xs text-gray-500 mb-1.5">{label}</label>
              <input
                type={type ?? 'text'}
                value={state[field]}
                onChange={(e) => update({ [field]: e.target.value })}
                placeholder={placeholder}
                className="w-full bg-[#233D4C] border border-[#2a4a5c] rounded-lg px-3 py-2.5 text-white text-sm placeholder:text-gray-600 focus:outline-none focus:border-[#FD802E] transition-colors"
              />
            </div>
          ))}
        </div>
      </div>

      {/* ── Evaluation Criteria ────────────────────────────────── */}
      <div className="space-y-3">
        <h3 className="text-xs uppercase text-gray-500 font-semibold tracking-wide px-1">
          Evaluation Criteria
        </h3>

        {state.criteria.map((criterion) => (
          <div
            key={criterion.id}
            className={`bg-[#02182B] border rounded-xl p-5 transition-colors ${
              criterion.score !== null ? 'border-[#FD802E]/20' : 'border-[#2a4a5c]'
            }`}
          >
            <div className="flex flex-col sm:flex-row sm:items-start gap-4">
              {/* Title + description */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="text-white font-semibold text-sm">{criterion.title}</h4>
                  {criterion.score !== null && (
                    <span className="shrink-0 px-2 py-0.5 bg-[#FD802E]/10 text-[#FD802E] rounded-full text-xs font-medium">
                      {criterion.score}/5
                    </span>
                  )}
                </div>
                {criterion.description && (
                  <p className="text-gray-500 text-xs leading-relaxed">{criterion.description}</p>
                )}
              </div>

              {/* Score buttons */}
              <div className="shrink-0">
                <p className="text-xs text-gray-600 mb-2 hidden sm:block text-right">Score</p>
                <div className="flex items-center gap-1.5">
                  {[1, 2, 3, 4, 5].map((v) => (
                    <ScoreButton
                      key={v}
                      value={v}
                      selected={criterion.score === v}
                      onClick={() => updateCriterionScore(criterion.id, v)}
                    />
                  ))}
                </div>
                {criterion.score !== null && (
                  <p className="text-xs text-gray-500 text-right mt-1.5">
                    {SCORE_LABELS[criterion.score]}
                  </p>
                )}
              </div>
            </div>

            {/* Notes */}
            <div className="mt-4">
              <textarea
                value={criterion.notes}
                onChange={(e) => updateCriterionNotes(criterion.id, e.target.value)}
                placeholder="Evidence / notes from interview…"
                rows={2}
                className="w-full bg-[#233D4C] border border-[#2a4a5c] rounded-lg px-3 py-2.5 text-white text-sm placeholder:text-gray-600 focus:outline-none focus:border-[#FD802E]/60 transition-colors resize-none leading-relaxed"
              />
            </div>
          </div>
        ))}
      </div>

      {/* ── Overall Assessment ─────────────────────────────────── */}
      <div className="bg-[#02182B] border border-[#2a4a5c] rounded-xl p-5 space-y-5">
        <h3 className="text-xs uppercase text-gray-500 font-semibold tracking-wide">
          Overall Assessment
        </h3>

        {/* Score bar */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-400">Total score</span>
            <span className={`text-2xl font-bold ${scoreColor}`}>
              {totalScore}
              <span className="text-sm font-normal text-gray-500 ml-1">/ {maxScore}</span>
            </span>
          </div>
          <div className="h-2.5 bg-[#233D4C] rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${barColor}`}
              style={{ width: `${scorePct}%` }}
            />
          </div>
          <div className="flex items-center justify-between text-xs text-gray-600">
            <span>
              {scored.length} of {state.criteria.length} criteria scored
            </span>
            <span>{scorePct}%</span>
          </div>
        </div>

        {/* Recommendation */}
        <div>
          <p className="text-xs text-gray-500 mb-3">Final Recommendation</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {RECOMMENDATION_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() =>
                  update({ recommendation: state.recommendation === opt.value ? null : opt.value })
                }
                className={`
                  py-2.5 px-3 rounded-lg border text-sm font-semibold transition-all
                  ${
                    state.recommendation === opt.value
                      ? `${opt.bg} ${opt.border} ${opt.color} scale-[1.02]`
                      : 'bg-[#233D4C] border-[#2a4a5c] text-gray-400 hover:border-[#3a5a6c]'
                  }
                `}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Export */}
        <div className="pt-2 border-t border-[#2a4a5c]">
          <button
            type="button"
            onClick={handleExport}
            className="flex items-center gap-2 bg-[#FD802E] text-[#233D4C] font-semibold text-sm px-5 py-2.5 rounded-lg hover:bg-[#ff8f45] transition-colors"
          >
            <Download size={14} /> Export to Word
          </button>
        </div>
      </div>
    </div>
  )
}
